import {
  ListItem,
  ListItemButton,
  type ListItemProps,
  ListItemText,
  ListItemTextProps,
  styled,
  Tooltip,
} from "@mui/material";
import isFocusVisible from "@mui/utils/isFocusVisible";
import { MdsCheck, MdsChevronRight } from "@sinch/icons";
import { isArray, isNotNilOrEmpty } from "ramda-adjunct";
import React, { forwardRef, ReactNode, Ref, type RefAttributes, useEffect, useRef, useState } from "react";
import { useWatchDependency } from "@sinch/hooks/utilities/useWatchDependency";
import { ConditionalWrapper } from "@sinch/components/ConditionalWrapper/ConditionalWrapper";
import { InlinedItems } from "@sinch/components/form/MultilevelSelect/InlinedItems";
import { ListColumnProps, RenderedOptions, SearchResultIndices } from "@sinch/components/form/MultilevelSelect/types";
import { MdsIcon } from "@sinch/components/MdsIcon";

export interface ListItemWrapperProps<TValue = any> {
  /** The value associated with this list item */
  value: TValue;
  /** The label to display, can be a single index or an array of indices */
  label: SearchResultIndices | SearchResultIndices[];
  description?: ReactNode;
  /** Handler for mouse over events */
  onMouseOver?: () => void;
  onItemHover?: () => void;
  /** Handler for click events */
  onSelectItem: ListColumnProps<TValue>["onChange"];
  /** Whether the item is in hover state */
  hoverState?: boolean;
  /** Whether the item is selected */
  selected?: boolean;
  /** Additional props to pass to the MUI ListItem component */
  listItemProps?: ListItemProps;
  hasChildren?: boolean;
  focused?: boolean;
  renderItem?: undefined | ((item: Pick<RenderedOptions<TValue>, "value" | "label">) => ReactNode);
  isOnValuePath?: boolean;
}

/**
 * Base implementation of the list item wrapper.
 * Renders a list item with appropriate styling for selection state.
 */
const ListItemWrapperBase = <TValue,>(
  {
    value,
    label,
    description,
    onMouseOver,
    onItemHover,
    onSelectItem,
    selected,
    listItemProps,
    hasChildren,
    focused: initFocused = false,
    renderItem,
    isOnValuePath,
  }: ListItemWrapperProps<TValue>,
  ref: Ref<HTMLLIElement>
) => {
  const [focused, setFocused] = useState<boolean>(initFocused);
  const btnAction = useRef<{ focusVisible: () => void } | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  const timeout = useRef<NodeJS.Timeout>(undefined);

  // Delay mouse over execution for cases when user want to select something in other column
  const handleMouseOver = () => {
    onMouseOver?.();
    timeout.current = setTimeout(() => {
      onItemHover?.();
    }, 80);
  };

  useEffect(() => {
    if (btnRef.current && isFocusVisible(btnRef.current)) {
      setFocused(true);
    }
  }, []);

  useEffect(() => {
    if (focused) {
      btnRef.current?.focus();
      btnAction.current?.focusVisible();
    }
  }, [focused]);

  useWatchDependency(() => {
    setFocused(initFocused);
  }, [initFocused]);

  useEffect(() => {
    if (!initFocused) {
      btnRef.current?.blur();
    }
  }, [initFocused]);

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === "Enter" && focused) {
      onSelectItem?.(e, value);
    }
  };
  return (
    <ListItem
      ref={ref}
      disablePadding
      onClick={(e) => onSelectItem?.(e, value)}
      onMouseOut={() => {
        if (timeout.current) {
          clearTimeout(timeout.current);
        }
      }}
      onMouseOver={handleMouseOver}
      {...listItemProps}
    >
      <ListItemButton
        ref={btnRef}
        action={btnAction}
        component="button"
        onKeyDown={handleKey}
        selected={selected || isOnValuePath}
      >
        <ConditionalWrapper
          condition={isNotNilOrEmpty(description)}
          wrapper={(children) => (
            <Tooltip
              enterDelay={1000}
              placement="left"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["right"], // Only flip to left if right doesn't fit
                        altBoundary: true,
                      },
                    },
                  ],
                },
              }}
              title={description}
            >
              {children}
            </Tooltip>
          )}
        >
          <ListItemTextNoMargin
            primary={
              isArray(label) ? (
                <InlinedItems label={label as SearchResultIndices[]} />
              ) : (
                (renderItem?.({ label, value }) ?? label)
              )
            }
            secondary={description}
          />
        </ConditionalWrapper>
        {selected && <MdsIcon icon={MdsCheck} />}
        {hasChildren && <MdsIcon fontSize="small" icon={MdsChevronRight} sx={{ color: "grey.600" }} />}
      </ListItemButton>
    </ListItem>
  );
};

export const ListItemWrapper = forwardRef(ListItemWrapperBase) as <TValue>(
  props: ListItemWrapperProps<TValue> & RefAttributes<HTMLLIElement>
) => ReturnType<typeof ListItemWrapperBase>;

const ListItemTextNoMargin = styled(ListItemText)<ListItemTextProps>({
  margin: "0 0 0 0",
  "&>.MuiListItemText-secondary": {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    overflow: "hidden",
  },
});
